import { useMemo } from "react";

import clsx from "clsx";

import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type DisplayColumnDef,
  type SortingState,
} from "@tanstack/react-table";

import { BodyCell } from "./body-cell";
import {
  ClickRowTitleFeature,
  ExpandRowFeature,
  LoadMoreFeature,
  OpenFlyoutFeature,
} from "./features";
import { HeaderCell } from "./header-cell";

interface Props<T> {
  columns: DisplayColumnDef<T>[];
  rows: T[];
  sorting?: SortingState;
  pinnedColumnIds?: string[];
  expandedRowIds?: string[];
  headerCellClassName?: string;
  bodyRowEvenClassName?: string;
  bodyRowOddClassName?: string;
  bodyCellClassName?: string;
  getRowId: (row: T) => string;
  getSubRows?: (row: T, index: number) => T[] | undefined;
  onSortingChange?: (sorting: SortingState) => void;
  onExpandRow?: (row: T) => void;
  onClickRowTitle?: (row: T) => void;
  onLoadMore?: (row: T) => void;
  onOpenFlyout?: (row: T) => void;
}

export const CoreTable = <T,>({
  columns,
  rows,
  sorting,
  pinnedColumnIds,
  expandedRowIds,
  headerCellClassName,
  bodyRowEvenClassName,
  bodyRowOddClassName,
  bodyCellClassName,
  getRowId,
  getSubRows,
  onSortingChange,
  onExpandRow,
  onClickRowTitle,
  onLoadMore,
  onOpenFlyout,
}: Props<T>) => {
  const columnPinning = useMemo(() => {
    return { left: pinnedColumnIds };
  }, [pinnedColumnIds?.join("")]);

  const expanded = useMemo(() => {
    return expandedRowIds?.reduce((acc, val) => {
      acc[val] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }, [expandedRowIds]);

  const table = useReactTable({
    _features: [
      ClickRowTitleFeature,
      ExpandRowFeature,
      LoadMoreFeature,
      OpenFlyoutFeature,
    ],
    columns,
    data: rows,
    state: {
      expandedRowIds,
      expanded,
      sorting,
      columnPinning,
    },
    enableSortingRemoval: false,
    getRowId,
    getSubRows,
    onSortingChange: onSortingChange
      ? (updater) => {
          onSortingChange(
            updater instanceof Function ? updater(sorting ?? []) : updater,
          );
        }
      : undefined,
    onExpandRow,
    onClickRowTitle,
    onLoadMore,
    onOpenFlyout,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <table
      className="table-fixed border-separate border-spacing-0"
      style={{
        width: table.getTotalSize(),
      }}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <HeaderCell
                key={header.id}
                header={header}
                className={headerCellClassName}
              />
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <tr
            key={row.id}
            className={clsx(
              rowIndex % 2 ? bodyRowEvenClassName : bodyRowOddClassName,
            )}
          >
            {row.getVisibleCells().map((cell) => (
              <BodyCell
                key={cell.id}
                cell={cell}
                className={bodyCellClassName}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
